import * as React from 'react'
import { ChevronDown } from 'lucide-react'
import { COUNTRIES } from '../countries'
import { DisclosureToggle, Label, inputClass } from './formParts'

const COMPANY_FIELDS_ID = 'checkout-company-fields'

// Country (tax is computed from it), and the optional company name and VAT ID.
// `vat` is the state of useVatField.
export default function BillingDetailsFields({
    details,
    onChangeDetails,
    vat,
}) {
    const [companyOpen, setCompanyOpen] = React.useState(
        Boolean(details.company || details.vatNumber)
    )
    const set = field => event =>
        onChangeDetails({ ...details, [field]: event.target.value })

    return (
        <>
            <label className="tw-flex tw-flex-col tw-gap-1">
                <Label>Country</Label>
                <div className="tw-relative">
                    {/* The native arrow ignores padding and hugs the border, so
                        it is hidden and drawn at the field's inner padding. */}
                    <select
                        data-testid="billing-country"
                        className={`${inputClass} tw-cursor-pointer tw-appearance-none tw-pr-9`}
                        value={details.country}
                        onChange={set('country')}
                        required
                    >
                        <option value="">Choose your country</option>
                        {COUNTRIES.map(([code, name]) => (
                            <option key={code} value={code}>
                                {name}
                            </option>
                        ))}
                    </select>
                    <ChevronDown
                        aria-hidden="true"
                        className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-3 tw-my-auto tw-h-4 tw-w-4 tw-text-muted-foreground"
                    />
                </div>
            </label>

            <div>
                <DisclosureToggle
                    open={companyOpen}
                    onToggle={() => setCompanyOpen(value => !value)}
                    controls={COMPANY_FIELDS_ID}
                    testId="billing-company-toggle"
                >
                    I'm buying for a company
                </DisclosureToggle>
                {companyOpen ? (
                    <div
                        id={COMPANY_FIELDS_ID}
                        className={`tw-mt-2 tw-grid tw-grid-cols-1 tw-gap-2 ${
                            vat.applies ? 'sm:tw-grid-cols-2' : ''
                        }`}
                    >
                        <input
                            className={inputClass}
                            placeholder="Company name"
                            aria-label="Company name"
                            value={details.company}
                            onChange={set('company')}
                            data-testid="billing-company"
                        />
                        {vat.applies ? (
                            // Checked with VIES on blur, not on every keystroke.
                            <input
                                className={inputClass}
                                placeholder="VAT ID (optional)"
                                aria-label="VAT ID (optional)"
                                aria-invalid={vat.status === 'invalid'}
                                value={vat.draft}
                                onChange={event =>
                                    vat.change(event.target.value)
                                }
                                onBlur={vat.commit}
                                onKeyDown={event => {
                                    if (event.key !== 'Enter') return
                                    event.preventDefault()
                                    vat.commit()
                                }}
                                data-testid="billing-vat"
                            />
                        ) : null}
                    </div>
                ) : null}
                {companyOpen ? <VatStatus vat={vat} /> : null}
            </div>
        </>
    )
}

function VatStatus({ vat }) {
    if (vat.status === 'checking')
        return (
            <p
                className="tw-m-0 tw-mt-2 tw-text-sm tw-text-muted-foreground"
                data-testid="vat-checking"
            >
                Checking the VAT number…
            </p>
        )
    if (vat.status === 'invalid')
        return (
            <p
                className="tw-m-0 tw-mt-2 tw-text-sm tw-text-destructive"
                role="alert"
                data-testid="vat-error"
            >
                {vat.error}
            </p>
        )
    if (vat.status === 'unverified')
        return (
            <p
                className="tw-m-0 tw-mt-2 tw-text-sm tw-text-muted-foreground"
                data-testid="vat-unverified"
            >
                We couldn't verify this VAT number right now, so VAT is included
                in the price.{' '}
                <button
                    type="button"
                    onClick={vat.recheck}
                    data-testid="vat-recheck"
                    className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-text-primary tw-underline-offset-4 hover:tw-underline"
                >
                    Check again
                </button>
            </p>
        )
    if (vat.status === 'valid')
        return (
            <p
                className="tw-m-0 tw-mt-2 tw-text-sm tw-text-success"
                data-testid="vat-valid"
            >
                VAT number verified.
            </p>
        )
    return null
}
